import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as L from 'leaflet';
import { ButtonModule } from 'primeng/button';

export interface IGps {
  gpsX: number;
  gpsY: number;
}

class NetstorkTile extends L.TileLayer.WMS {
  constructor(
    options: L.WMSOptions,
    private fetchTileUrl: (coords: L.Coords) => Promise<string | null>,
  ) {
    super('https://not-uses/{z}/{x}/{y}', options);
  }

  override createTile(
    coords: L.Coords,
    done: (err?: Error, tile?: HTMLElement) => void,
  ): HTMLElement {
    const img = document.createElement('img');
    img.setAttribute('role', 'presentation');

    this.fetchTileUrl(coords)
      .then((tileUrl) => {
        if (tileUrl) {
          img.src = tileUrl;
          done(undefined, img);
        } else {
          img.style.background = 'rgba(255, 255, 255, 0.5)';
          done();
        }
      })
      .catch((error) => done(error));
    return img;
  }
}

@Component({
  selector: 'kon-netstork-map',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `<div class="relative w-full h-full">
    <div id="map" class="w-full h-full"></div>
    @if(showMarker()){
    <p-button
      label="OK"
      (onClick)="emitPinnedLocation()"
      class="absolute bottom-6 right-6 z-[500]"
    />
    }
  </div>`,
  styles: `::ng-deep .leaflet-container img.leaflet-tile {
    mix-blend-mode: unset;
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetstorkMapComponent implements AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  map!: L.Map;
  marker: L.Marker | undefined;

  gpsSelected = output<IGps>();

  getTileUrl = input<(coords: L.Coords) => Promise<string | null>>();

  showMarker = input<boolean>(true);

  shown = signal(false);

  private viewReady = signal(false);

  initMap(): void {
    const defaultY = 48.573; // Default latitude
    const defaultX = 19.602; // Default longitude

    const x = this.route.snapshot.queryParams['x'] ?? undefined;
    const y = this.route.snapshot.queryParams['y'] ?? undefined;
    const zoom = this.route.snapshot.queryParams['zoom'] ?? undefined;
    const minZoom = 8;
    const maxZoom = 19;

    if (this.map) {
      this.map.remove();
    }

    this.map = L.map('map').setView(
      [y || defaultY, x || defaultX],
      zoom ? +zoom : minZoom,
    );

    if (this.showMarker()) {
      this.marker = L.marker([y || defaultY, x || defaultX], {
        draggable: true,
        autoPan: true,
        icon: L.icon({
          iconUrl:
            'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48"><path fill="red" stroke="black" stroke-width="2" d="M16 2C9.373 2 4 7.373 4 14c0 8.284 10.373 29.373 11.293 31.293a1 1 0 0 0 1.414 0C17.627 43.373 28 22.284 28 14c0-6.627-5.373-12-12-12zm0 18a6 6 0 1 1 0-12 6 6 0 0 1 0 12z"/></svg>',
          iconSize: [32, 48],
          iconAnchor: [16, 48],
        }),
      }).addTo(this.map);
    }

    const tileOptions = {
      tileSize: 256,
      zoomOffset: 0,
      minZoom,
      maxZoom,
    };

    L.tileLayer(
      `https://tile.openstreetmap.org/{z}/{x}/{y}.png`,
      tileOptions,
    ).addTo(this.map);

    if (this.getTileUrl()) {
      new NetstorkTile(tileOptions, this.getTileUrl()!).addTo(this.map);
    }

    this.map.on('moveend', (a) => {
      const center = a.target.getCenter();
      const zoom = a.target.getZoom();
      this.router.navigate(['.'], {
        relativeTo: this.route,
        queryParams: { x: center.lng, y: center.lat, zoom },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });

    this.map.on('click', (e: L.LeafletMouseEvent) => {
      if (this.marker) {
        this.marker.setLatLng(e.latlng);
      }
    });
  }

  emitPinnedLocation() {
    const latlng = this.marker?.getLatLng();
    if (latlng) {
      this.gpsSelected.emit({
        gpsX: latlng.lng,
        gpsY: latlng.lat,
      });
    }
  }

  private initEffect = effect(() => {
    if (!this.shown() || !this.viewReady()) {
      return;
    }

    queueMicrotask(() => {
      this.initMap();
      this.map.invalidateSize();
    });
  });

  showMap() {
    this.shown.set(true);
  }

  ngAfterViewInit() {
    this.viewReady.set(true);
  }
}
